import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Target, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  TrendingUp,
  Users,
  Activity,
  BarChart3
} from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface AdvertiserTracking {
  id: string;
  advertiser_name: string;
  tag_type: 'meta' | 'google' | 'none' | 'cruzasmart';
  tag_status: 'active' | 'missing' | 'inactive';
  last_activity: string | null;
  total_impressions: number;
  total_clicks: number;
  ctr: number;
  meta_pixel_id?: string;
  google_tag_id?: string;
  fallback_tag?: string;
  days_inactive: number;
}

const AdminTrackingSummary = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const { toast } = useToast();
  
  const [advertisers, setAdvertisers] = useState<AdvertiserTracking[]>([]);
  const [filteredAdvertisers, setFilteredAdvertisers] = useState<AdvertiserTracking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagTypeFilter, setTagTypeFilter] = useState('all');
  const [editingAdvertiser, setEditingAdvertiser] = useState<AdvertiserTracking | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Global metrics
  const [metrics, setMetrics] = useState({
    totalAdvertisers: 0,
    percentWithTracking: 0,
    percentActive7Days: 0,
    topPerforming: [] as { name: string; ctr: number }[]
  });

  useEffect(() => {
    loadTrackingData();
  }, []);

  useEffect(() => {
    filterAdvertisers();
  }, [advertisers, searchTerm, statusFilter, tagTypeFilter]);

  const loadTrackingData = async () => {
    try {
      // Fetch advertisers from Supabase
      const { data: advertisersData, error: advertisersError } = await supabase
        .from('advertisers')
        .select('*')
        .eq('is_active', true);
      
      if (advertisersError) throw advertisersError;

      // For each advertiser, get their ads data
      const trackingPromises = (advertisersData || []).map(async (advertiser) => {
        const { data: adsData } = await supabase
          .from('ads')
          .select('*')
          .eq('company_name', advertiser.company_name);

        const totalImpressions = (adsData || []).reduce((sum, ad) => sum + (ad.impressions || 0), 0);
        const totalClicks = (adsData || []).reduce((sum, ad) => sum + (ad.clicks || 0), 0);
        const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
        
        // Calculate days inactive
        const lastActivity = advertiser.last_activity ? new Date(advertiser.last_activity) : null;
        const daysInactive = lastActivity ? Math.floor((Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24)) : 0;

        return {
          id: advertiser.id,
          advertiser_name: advertiser.company_name,
          tag_type: advertiser.meta_pixel_id ? 'meta' as const : advertiser.google_tag_id ? 'google' as const : 'none' as const,
          tag_status: (advertiser.tracking_status || 'missing') as 'active' | 'inactive' | 'missing',
          last_activity: advertiser.last_activity || null,
          total_impressions: totalImpressions,
          total_clicks: totalClicks,
          ctr: Number(ctr.toFixed(2)),
          meta_pixel_id: advertiser.meta_pixel_id || undefined,
          google_tag_id: advertiser.google_tag_id || undefined,
          fallback_tag: advertiser.default_tracking_pixel_enabled ? 'cruzasmart_default_tracking' : undefined,
          days_inactive: daysInactive
        };
      });

      const trackingData = await Promise.all(trackingPromises);
      setAdvertisers(trackingData);
      
      // Calculate metrics
      const totalAds = trackingData.length;
      const withTracking = trackingData.filter(a => a.tag_type !== 'none').length;
      const activeIn7Days = trackingData.filter(a => a.days_inactive <= 7).length;
      const topPerforming = trackingData
        .sort((a, b) => b.ctr - a.ctr)
        .slice(0, 3)
        .map(a => ({ name: a.advertiser_name, ctr: a.ctr }));

      setMetrics({
        totalAdvertisers: totalAds,
        percentWithTracking: totalAds > 0 ? Math.round((withTracking / totalAds) * 100) : 0,
        percentActive7Days: totalAds > 0 ? Math.round((activeIn7Days / totalAds) * 100) : 0,
        topPerforming
      });
    } catch (error) {
      console.error('Error loading tracking data:', error);
      toast({
        title: isSpanish ? "Error" : "Error",
        description: isSpanish ? "Error al cargar datos de seguimiento" : "Failed to load tracking data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterAdvertisers = () => {
    let filtered = [...advertisers];

    if (searchTerm) {
      filtered = filtered.filter(a => 
        a.advertiser_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.tag_status === statusFilter);
    }

    if (tagTypeFilter !== 'all') {
      filtered = filtered.filter(a => a.tag_type === tagTypeFilter);
    }

    setFilteredAdvertisers(filtered);
  };

  const getTagTypeDisplay = (type: string) => {
    switch (type) {
      case 'meta': return 'Meta Pixel';
      case 'google': return 'Google Tag';
      case 'cruzasmart': return 'CruzaSmart';
      case 'none': 
      default: return isSpanish ? 'Ninguno' : 'None';
    }
  };

  const getStatusBadge = (status: string, daysInactive: number) => {
    if (status === 'active') {
      return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />{isSpanish ? 'Activo' : 'Active'}</Badge>;
    }
    if (status === 'missing') {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />{isSpanish ? 'Faltante' : 'Missing'}</Badge>;
    }
    if (status === 'inactive') {
      const variant = daysInactive > 14 ? "destructive" : "secondary";
      const icon = daysInactive > 14 ? <AlertTriangle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />;
      return (
        <Badge variant={variant}>
          {icon}
          {isSpanish ? 'Inactivo' : 'Inactive'}
          {daysInactive > 14 && ` (${daysInactive}d)`}
        </Badge>
      );
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  const exportToCSV = () => {
    const headers = [
      'Advertiser Name',
      'Tag Type', 
      'Tag Status',
      'Last Activity',
      'Total Impressions',
      'Total Clicks',
      'CTR (%)',
      'Meta Pixel ID',
      'Google Tag ID',
      'Fallback Tag'
    ];

    const csvData = filteredAdvertisers.map(advertiser => [
      advertiser.advertiser_name,
      getTagTypeDisplay(advertiser.tag_type),
      advertiser.tag_status,
      advertiser.last_activity ? format(new Date(advertiser.last_activity), 'yyyy-MM-dd HH:mm') : 'Never',
      advertiser.total_impressions,
      advertiser.total_clicks,
      advertiser.ctr.toFixed(2),
      advertiser.meta_pixel_id || '',
      advertiser.google_tag_id || '',
      advertiser.fallback_tag || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tracking_summary_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();

    toast({
      title: isSpanish ? "Exportado" : "Exported",
      description: isSpanish ? "Reporte exportado exitosamente" : "Report exported successfully"
    });
  };

  const handleEditAdvertiser = (advertiser: AdvertiserTracking) => {
    setEditingAdvertiser(advertiser);
    setIsEditDialogOpen(true);
  };

  const saveFallbackTag = async () => {
    if (!editingAdvertiser) return;

    try {
      // In real implementation, save to Supabase
      const updatedAdvertisers = advertisers.map(a => 
        a.id === editingAdvertiser.id ? editingAdvertiser : a
      );
      setAdvertisers(updatedAdvertisers);
      
      toast({
        title: isSpanish ? "Guardado" : "Saved",
        description: isSpanish ? "Configuración de seguimiento actualizada" : "Tracking configuration updated"
      });
      
      setIsEditDialogOpen(false);
      setEditingAdvertiser(null);
    } catch (error) {
      console.error('Error saving fallback tag:', error);
      toast({
        title: isSpanish ? "Error" : "Error",
        description: isSpanish ? "Error al guardar configuración" : "Failed to save configuration",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Target className="h-8 w-8" />
            {isSpanish ? 'Resumen de Seguimiento' : 'Tracking Summary'}
          </h1>
          <p className="text-muted-foreground">
            {isSpanish ? 'Supervisa el estado del seguimiento de anunciantes y el rendimiento' : 'Monitor advertiser tracking status and performance'}
          </p>
        </div>
      </div>

      {/* Global Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Total Anunciantes' : 'Total Advertisers'}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalAdvertisers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? '% con Seguimiento' : '% with Tracking'}
            </CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{metrics.percentWithTracking}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? '% Activos (7 días)' : '% Active (7 days)'}
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{metrics.percentActive7Days}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isSpanish ? 'Mejor CTR' : 'Top CTR'}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {metrics.topPerforming[0]?.ctr.toFixed(2)}%
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {metrics.topPerforming[0]?.name}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performing Ads */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {isSpanish ? 'Top 3 Anuncios Destacados' : 'Top 3 Performing Ads'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {metrics.topPerforming.map((ad, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                <span className="font-medium">#{index + 1} {ad.name}</span>
                <Badge variant="outline">{ad.ctr.toFixed(2)}% CTR</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>{isSpanish ? 'Filtros y Búsqueda' : 'Filters & Search'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={isSpanish ? "Buscar anunciante..." : "Search advertiser..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder={isSpanish ? "Estado" : "Status"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isSpanish ? 'Todos los Estados' : 'All Status'}</SelectItem>
                <SelectItem value="active">{isSpanish ? 'Activo' : 'Active'}</SelectItem>
                <SelectItem value="inactive">{isSpanish ? 'Inactivo' : 'Inactive'}</SelectItem>
                <SelectItem value="missing">{isSpanish ? 'Faltante' : 'Missing'}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={tagTypeFilter} onValueChange={setTagTypeFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder={isSpanish ? "Tipo de Tag" : "Tag Type"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isSpanish ? 'Todos los Tipos' : 'All Types'}</SelectItem>
                <SelectItem value="meta">Meta Pixel</SelectItem>
                <SelectItem value="google">Google Tag</SelectItem>
                <SelectItem value="cruzasmart">CruzaSmart</SelectItem>
                <SelectItem value="none">{isSpanish ? 'Ninguno' : 'None'}</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={exportToCSV} variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              {isSpanish ? 'Exportar CSV' : 'Export CSV'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advertiser Table */}
      <Card>
        <CardHeader>
          <CardTitle>{isSpanish ? 'Resumen de Tags de Anunciantes' : 'Advertiser Tag Overview'}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isSpanish ? 'Anunciante' : 'Advertiser Name'}</TableHead>
                <TableHead>{isSpanish ? 'Tipo de Tag' : 'Tag Type'}</TableHead>
                <TableHead>{isSpanish ? 'Estado' : 'Status'}</TableHead>
                <TableHead>{isSpanish ? 'Última Actividad' : 'Last Activity'}</TableHead>
                <TableHead>{isSpanish ? 'Impresiones' : 'Impressions'}</TableHead>
                <TableHead>CTR</TableHead>
                <TableHead>{isSpanish ? 'Acciones' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAdvertisers.map((advertiser) => (
                <TableRow key={advertiser.id}>
                  <TableCell className="font-medium">{advertiser.advertiser_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{getTagTypeDisplay(advertiser.tag_type)}</Badge>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(advertiser.tag_status, advertiser.days_inactive)}
                  </TableCell>
                  <TableCell>
                    {advertiser.last_activity ? (
                      <div className="text-sm">
                        {format(new Date(advertiser.last_activity), 'MMM dd, yyyy')}
                        <br />
                        <span className="text-muted-foreground">
                          {format(new Date(advertiser.last_activity), 'HH:mm')}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{isSpanish ? 'Nunca' : 'Never'}</span>
                    )}
                  </TableCell>
                  <TableCell>{advertiser.total_impressions.toLocaleString()}</TableCell>
                  <TableCell>
                    <span className={`font-medium ${advertiser.ctr > 3 ? 'text-green-600' : advertiser.ctr > 2 ? 'text-orange-600' : 'text-red-600'}`}>
                      {advertiser.ctr.toFixed(2)}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEditAdvertiser(advertiser)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isSpanish ? 'Editar Configuración de Seguimiento' : 'Edit Tracking Configuration'}
            </DialogTitle>
          </DialogHeader>
          
          {editingAdvertiser && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="advertiser-name">{isSpanish ? 'Anunciante' : 'Advertiser'}</Label>
                <Input 
                  id="advertiser-name"
                  value={editingAdvertiser.advertiser_name} 
                  readOnly 
                  className="bg-muted"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="meta-pixel">{isSpanish ? 'Meta Pixel ID' : 'Meta Pixel ID'}</Label>
                  <Input
                    id="meta-pixel"
                    value={editingAdvertiser.meta_pixel_id || ''}
                    onChange={(e) => setEditingAdvertiser({
                      ...editingAdvertiser,
                      meta_pixel_id: e.target.value
                    })}
                    placeholder="123456789012345"
                  />
                </div>

                <div>
                  <Label htmlFor="google-tag">{isSpanish ? 'Google Tag ID' : 'Google Tag ID'}</Label>
                  <Input
                    id="google-tag"
                    value={editingAdvertiser.google_tag_id || ''}
                    onChange={(e) => setEditingAdvertiser({
                      ...editingAdvertiser,
                      google_tag_id: e.target.value
                    })}
                    placeholder="GTM-XXXXXXX"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="fallback-tag">
                  {isSpanish ? 'Tag de Respaldo (Fallback)' : 'Fallback Tracking Tag'}
                </Label>
                <Textarea
                  id="fallback-tag"
                  value={editingAdvertiser.fallback_tag || ''}
                  onChange={(e) => setEditingAdvertiser({
                    ...editingAdvertiser,
                    fallback_tag: e.target.value
                  })}
                  placeholder={isSpanish ? 
                    "Tag o script personalizado para seguimiento de respaldo..." : 
                    "Custom tag or script for fallback tracking..."
                  }
                  rows={3}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {isSpanish ? 
                    'Se usará si el tag principal no está disponible o está inactivo > 14 días' :
                    'Will be used if primary tag is unavailable or inactive > 14 days'
                  }
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={saveFallbackTag}>
                  {isSpanish ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrackingSummary;